import {
  createClient,
  navigatorLock,
  NavigatorLockAcquireTimeoutError,
} from "@supabase/supabase-js";
import type { Database } from "./supabase-types";

const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
const explicitUrl = import.meta.env.VITE_SUPABASE_URL;
let SUPABASE_URL =
  typeof explicitUrl === "string" && explicitUrl.startsWith("http")
    ? explicitUrl
    : typeof projectId === "string" && projectId
      ? `https://${projectId}.supabase.co`
      : "";

if (!SUPABASE_URL && import.meta.env.DEV) {
  SUPABASE_URL = "https://cnphwhynfezadsobiixa.supabase.co";
  console.warn(
    "[Supabase] Using dev fallback URL. Set VITE_SUPABASE_URL or VITE_SUPABASE_PROJECT_ID in .env in the project root."
  );
}

// Only the publishable/anon key may be read here: every VITE_* variable read in
// src/ is inlined into the public bundle. No fallback to any other variable
// (COL-489/COL-523): a secret key once sat behind one.
const SUPABASE_PUBLISHABLE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "";

if (!SUPABASE_URL) {
  throw new Error(
    "Supabase URL is required. Set VITE_SUPABASE_URL or VITE_SUPABASE_PROJECT_ID in .env in the project root, then restart the dev server."
  );
}
if (!SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    "Supabase anon key is required. Set VITE_SUPABASE_PUBLISHABLE_KEY in .env (the publishable key from Supabase Dashboard → Project Settings → API Keys)."
  );
}

/** Base URL for Supabase (e.g. for calling Edge Functions with fetch). No trailing slash to avoid double-slash in paths. */
export const supabaseUrl = SUPABASE_URL.replace(/\/+$/, "");

/** Publishable/anon key — required on `apikey` header for Edge Function `fetch` calls (matches `functions.invoke`). */
export const supabaseAnonKey = SUPABASE_PUBLISHABLE_KEY;

/**
 * Time out auth requests — and only auth requests.
 *
 * Everything auth-js does (getSession, refreshSession, updateUser, verifyOtp)
 * runs inside one exclusive Web Lock keyed on the storage key, and `fetch` has no
 * timeout of its own. So a request that never settles doesn't just fail its own
 * call: it holds the lock, and every later auth call in the tab queues behind it
 * forever. That is what left /reset-password sitting on "Resetting Password…"
 * with no error and no way forward — `updateUser()` never settled.
 *
 * Storage uploads and long report queries are deliberately NOT capped here: a
 * slow document upload is normal and must not be aborted.
 */
const AUTH_REQUEST_TIMEOUT_MS = 12_000;

/**
 * Cap on how long we wait to acquire the auth lock.
 *
 * Web Locks are shared by every tab on the origin, and auth-js asks for the lock
 * with "wait forever" (`acquireTimeout: -1`). A tab that the browser froze
 * mid-refresh keeps holding it, so a freshly opened tab — the one from the reset
 * email, say — can wait indefinitely with nothing to show the user. Bounded
 * waiting turns that into a retryable error instead of a hang. Kept above
 * AUTH_REQUEST_TIMEOUT_MS so a legitimately in-flight auth request still gets to
 * finish before a queued caller gives up.
 */
const LOCK_ACQUIRE_TIMEOUT_MS = 20_000;

const requestUrl = (input: RequestInfo | URL): string =>
  typeof input === "string"
    ? input
    : input instanceof URL
      ? input.href
      : input.url;

const fetchWithAuthTimeout: typeof fetch = (input, init) => {
  if (!requestUrl(input).includes("/auth/v1/")) return fetch(input, init);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AUTH_REQUEST_TIMEOUT_MS);
  // Keep honouring a caller-supplied signal (postgrest/functions abort helpers).
  const callerSignal = init?.signal;
  const abort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) abort();
    else callerSignal.addEventListener("abort", abort, { once: true });
  }

  return fetch(input, { ...init, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
    callerSignal?.removeEventListener("abort", abort);
  });
};

/**
 * navigatorLock, but an infinite acquire timeout becomes a bounded one, and an
 * aborted acquire is reported as the error auth-js expects (`isAcquireTimeout`)
 * so its background refresh tick still treats a busy lock as "skip this tick"
 * rather than an unhandled failure.
 *
 * Left undefined where Web Locks are missing (older Safari, jsdom in tests) so
 * auth-js falls back to its own no-op lock instead of crashing on
 * `navigator.locks`.
 */
const boundedNavigatorLock = globalThis.navigator?.locks
  ? async <R>(
      name: string,
      acquireTimeout: number,
      fn: () => Promise<R>
    ): Promise<R> => {
      const timeout =
        acquireTimeout < 0 ? LOCK_ACQUIRE_TIMEOUT_MS : acquireTimeout;
      try {
        return await navigatorLock(name, timeout, fn);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          throw new NavigatorLockAcquireTimeoutError(
            `Acquiring an exclusive Navigator LockManager lock "${name}" timed out after ${timeout}ms`
          );
        }
        throw error;
      }
    }
  : undefined;

export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
      lock: boundedNavigatorLock,
    },
    global: {
      fetch: fetchWithAuthTimeout,
    },
  }
);
